import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X } from 'lucide-react';
import { formatCurrency } from '@/utils/gameLogic';

export const DebugPanel = ({ gameState, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      className="fixed top-0 right-0 h-screen w-96 bg-card border-l border-border shadow-2xl z-50 overflow-y-auto"
    >
      <Card className="border-0 rounded-none h-full">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Debug Mode</CardTitle>
              <CardDescription>Internal game state</CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Game Time */}
          <DebugSection title="Game Time">
            <DebugItem label="Game Time (seconds)" value={gameState.gameTime} />
          </DebugSection>
          
          {/* Queue State */}
          <DebugSection title="Customer Queue">
            <DebugItem label="In Queue" value={gameState.customers.length} />
            <DebugItem label="Handled" value={gameState.handledCustomers.length} />
            <div className="mt-2 space-y-1">
              {gameState.customers.map(customer => (
                <div key={customer.id} className="text-xs p-2 bg-muted rounded">
                  <strong>{customer.profile.name}</strong> ({customer.profile.tier})
                  <br />
                  {customer.trip.from} → {customer.trip.to}
                </div>
              ))}
            </div>
          </DebugSection>
          
          {/* Stats */}
          <DebugSection title="Statistics">
            <DebugItem label="Total CSAT" value={gameState.stats.totalCsat.toFixed(2)} />
            <DebugItem label="Customers Handled" value={gameState.stats.customersHandled} />
            <DebugItem label="Average CSAT" value={
              gameState.stats.customersHandled > 0 
                ? `${(gameState.stats.totalCsat / gameState.stats.customersHandled).toFixed(1)}%`
                : 'N/A'
            } />
            <DebugItem label="Budget Spent" value={formatCurrency(gameState.stats.budgetSpent)} />
            <DebugItem label="Avg Response Time" value={`${(gameState.stats.avgResponseTime / 60).toFixed(1)}m`} />
          </DebugSection>
          
          {/* Budget */}
          <DebugSection title="Budget">
            <DebugItem label="Remaining" value={formatCurrency(gameState.budget)} />
            <DebugItem label="Spent" value={formatCurrency(gameState.stats.budgetSpent)} />
          </DebugSection>
          
          {/* Handled Customers */}
          <DebugSection title="Handled Customers History">
            {gameState.handledCustomers.length === 0 ? (
              <p className="text-xs text-muted-foreground">No customers handled yet</p>
            ) : (
              <div className="space-y-2">
                {gameState.handledCustomers.map(customer => (
                  <div key={customer.id} className="text-xs p-2 bg-muted rounded">
                    <strong>{customer.profile.name}</strong>
                    <br />
                    Tier: {customer.profile.tier}
                    <br />
                    Route: {customer.trip.from} → {customer.trip.to}
                  </div>
                ))}
              </div>
            )}
          </DebugSection>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const DebugSection = ({ title, children }) => {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-3 text-[hsl(var(--primary))]">{title}</h3>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

const DebugItem = ({ label, value }) => {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-mono font-semibold">{value}</span>
    </div>
  );
};